/**
 * V2 Product Bundle JavaScript
 * Handles all functionality for the v2-main-product-bundle section
 */

class V2ProductBundle {
  constructor(sectionId, config) {
    this.sectionId = sectionId
    this.config = config
    this.$section = document.querySelector(`#shopify-section-${sectionId}`)

    if (!this.$section) {
      console.error(`Section not found: ${sectionId}`)
      return
    }

    // Cache DOM elements
    this.cacheElements()

    // Initialize features based on config
    this.init()
  }

  cacheElements() {
    // Purchase options
    this.$purchaseOptions = this.$section.querySelectorAll('[name="purchase-option"]')

    // Product options
    this.$$productOptions = this.$section.querySelectorAll('[name^="option"]')
    // Dynamic color images (works with any color)
    this.$$colorImages = this.$section.querySelectorAll('[data-color]')

    // Price elements
    this.$$price = this.$section.querySelectorAll('[data-price]')
    this.$compareAtPrice = this.$section.querySelectorAll('[data-compare-at-price]')
    this.$discountBadge = this.$section.querySelector('[data-discount-badge]')
    this.$bundlePrice = this.$section.querySelector('[data-bundle-price]')
    this.$onlyProductPrice = this.$section.querySelector('[data-product-only-price]')

    // Product info
    this.$productTitle = this.$section.querySelector('[data-product-title]')
    this.$productSubtitle = this.$section.querySelector('[data-product-subtitle]')
    this.$productDescription = this.$section.querySelector('[data-product-description]')
    this.$whatToExpect = this.$section.querySelector('[data-what-to-expect]')

    // Add to cart
    this.$$addToCartButtons = this.$section.querySelectorAll('[data-add-to-cart]')
    this.$$addonAddToCartButtons = this.$section.querySelectorAll('[data-addon-add-to-cart]')

    // Accordion
    this.$accordionToggles = this.$section.querySelectorAll('[data-accordion-toggle]')

    // Klaviyo
    this.$klaviyoFormTriggerButton = this.$section.querySelector('[data-klaviyo-form-trigger] button')

    // FAQ
    this.$faqLink = this.$section.querySelector('[href="#faq"]')
  }

  init() {
    // Initialize purchase options
    if (this.config.hideBundleOption !== true) {
      this.initPurchaseOptions()
    }

    // Initialize product options (color swatches)
    this.initProductOptions()

    // Initialize discount badge
    this.initDiscountBadge()

    // Initialize Klaviyo forms
    if (this.config.showKlaviyoFormTrigger) {
      this.initKlaviyoForms()
    }

    // Initialize Affirm
    if (this.config.showAffirmPrice) {
      this.initAffirm()
    }

    // Initialize add to cart
    this.initAddToCart()

    // Initialize addon add to cart
    this.initAddonAddToCart()

    // Initialize accordions
    this.initAccordions()

    // Initialize FAQ link
    this.initFaqLink()

    // Initialize shipping text visibility
    this.updateShippingTextVisibility()

    // Initialize ATC availability for current variant
    this.updateAddToCartAvailability()

    // Ensure carousel syncs with initial variant selection (from URL parameters)
    this.syncCarouselWithCurrentVariant()
  }

  // Purchase Options Management
  initPurchaseOptions() {
    if (!this.$purchaseOptions.length) return

    // Store original content
    this.originalProductSubtitle = this.$productSubtitle?.textContent
    this.originalProductDescription = this.$productDescription?.textContent
    this.originalWhatToExpect = this.$whatToExpect?.innerHTML

    this.$purchaseOptions.forEach(($purchaseOption) => {
      $purchaseOption.addEventListener('change', () => {
        if ($purchaseOption.checked) {
          this.handlePurchaseOptionChange($purchaseOption.value)
        }
      })
    })
  }

  handlePurchaseOptionChange(value) {
    if (value === 'product-only') {
      // Update prices
      this.$$price.forEach(($price) => {
        $price.textContent = this.config.onlyProductPrice
      })
      this.$compareAtPrice.forEach(($compareAtPrice) => {
        $compareAtPrice.textContent = this.config.onlyProductCompareAtPrice
      })
      this.updateDiscountBadge(this.config.onlyProductDiscount)

      // Update title
      if (this.$productTitle) {
        this.$productTitle.textContent = this.config.onlyProductTitle
      }

      // Update media gallery
      this.updateCarouselImages()

      // Handle Affirm
      if (this.config.showAffirmPrice) {
        this.handleAffirmForProductOnly()
      }
    } else {
      // Bundle selected
      this.$$price.forEach(($price) => {
        $price.textContent = this.config.bundlePrice
      })
      this.$compareAtPrice.forEach(($compareAtPrice) => {
        $compareAtPrice.textContent = this.config.bundleCompareAtPrice
      })
      this.updateDiscountBadge(this.config.bundleDiscount)

      if (this.$productTitle) {
        this.$productTitle.textContent = this.config.bundleTitle
      }

      // Update media gallery
      this.updateCarouselImages()

      // Handle Affirm
      if (this.config.showAffirmPrice) {
        this.handleAffirmForBundle()
      }
    }
  }

  // Product Options (Color) Management
  initProductOptions() {
    if (!this.$$productOptions.length) return

    this.$$productOptions.forEach(($productOption) => {
      $productOption.addEventListener('change', () => {
        this.handleProductOptionChange($productOption)
      })
    })
  }

  handleProductOptionChange($productOption) {
    // Update displayed value
    const $optionValue = this.$section.querySelector(`[data-option-value="${$productOption.dataset.optionName}"]`)
    if ($optionValue) {
      $optionValue.textContent = $productOption.value.toLowerCase()
    }

    // Dynamic color handling - works with ANY color
    if ($productOption.dataset.optionName === 'color') {
      const selectedColor = this.handleizeColor($productOption.value)

      this.$$colorImages.forEach(($img) => {
        const imgColor = $img.dataset.color
        // Show if matches selected color OR is neutral (empty string)
        if (imgColor === selectedColor || imgColor === '') {
          $img.classList.remove('hidden')
        } else {
          $img.classList.add('hidden')
        }
      })
    }

    // Update media gallery based on mode
    if (this.config.useBlocks) {
      // Image blocks mode: filter by label and color
      this.updateCarouselImages()
    } else {
      // Product media mode: navigate to variant's featured media
      this.goToVariantMedia()
    }

    // Update shipping text based on selected variant
    this.updateShippingTextVisibility()

    // Update ATC availability for selected variant
    this.updateAddToCartAvailability()
  }

  updateAddToCartAvailability() {
    if (!this.$$addToCartButtons.length) return

    const selectedVariant = this.getSelectedVariant()
    if (!selectedVariant) return

    const isAvailable = selectedVariant.available && !this.config.isOutOfStock
    const label = !isAvailable
      ? 'Out of Stock'
      : window.location.pathname === '/pages/shaving-lander'
        ? 'Shop Now'
        : 'Add To Cart'

    this.$$addToCartButtons.forEach(($button) => {
      $button.disabled = !isAvailable
      const $span = $button.querySelector('span')
      if ($span) $span.textContent = label
    })
  }

  // Shipping Text Management
  updateShippingTextVisibility() {
    // Get all shipping text spans
    const $$shippingSpans = this.$section.querySelectorAll('[data-variant-id]')

    if (!$$shippingSpans.length) return

    // Get currently selected variant
    const $$selectedProductOptions = this.$section.querySelectorAll('[name^="option"]:checked')
    const selectedProductOptions = [...$$selectedProductOptions].map(($opt) => $opt.value)

    const selectedVariant = this.config.variants.find((variant) => {
      return selectedProductOptions.every((option, index) => {
        return variant.options[index] === option
      })
    })

    if (!selectedVariant) return

    // Show/hide shipping text based on selected variant
    $$shippingSpans.forEach(($span) => {
      const variantId = parseInt($span.dataset.variantId)
      if (variantId === selectedVariant.id) {
        $span.classList.remove('hidden')
      } else {
        $span.classList.add('hidden')
      }
    })
  }

  // Discount Badge Management
  initDiscountBadge() {
    if (this.$discountBadge) {
      this.updateDiscountBadge(this.$discountBadge.textContent)
    }
  }

  updateDiscountBadge(text) {
    if (!this.$discountBadge) return

    if (!text || text.trim() === '') {
      this.$discountBadge.classList.add('hidden')
      return
    }

    const inventory = this.config.currentVariantInventory || 0
    const inventoryRemaining = Math.max(2000 + inventory, 10)
    this.$discountBadge.textContent = text.replace('2,OOO', inventoryRemaining.toLocaleString().replace('0', 'O'))
    this.$discountBadge.classList.remove('hidden')
  }

  // Klaviyo Form Management
  initKlaviyoForms() {
    if (!this.$klaviyoFormTriggerButton) return

    this.intelligemsReady(() => {
      this.setupKlaviyoFormTrigger()
    })
  }

  intelligemsReady(callback) {
    if (!window.igData) {
      window.addEventListener('ig:ready', callback)
    } else {
      callback()
    }
  }

  setupKlaviyoFormTrigger() {
    // Get form IDs
    let klaviyoDesktopFormId = this.config.klaviyoFormDesktopId
    let klaviyoMobileFormId = this.config.klaviyoFormMobileId

    // Override with Intelligems test values if available
    if (window.klaviyoDesktopFormID) {
      klaviyoDesktopFormId = window.klaviyoDesktopFormID
    }
    if (window.klaviyoMobileFormID) {
      klaviyoMobileFormId = window.klaviyoMobileFormID
    }

    // Get button text
    let klaviyoFormTriggerBeforeText = this.config.klaviyoFormTriggerText
    let klaviyoFormTriggerAfterText = this.config.klaviyoFormTriggerTextSubmitted

    if (window.klaviyoPDPCalloutTextBefore) {
      klaviyoFormTriggerBeforeText = window.klaviyoPDPCalloutTextBefore
    }
    if (window.klaviyoPDPCalloutTextAfter) {
      klaviyoFormTriggerAfterText = window.klaviyoPDPCalloutTextAfter
    }

    this.$klaviyoFormTriggerButton.innerHTML = klaviyoFormTriggerBeforeText

    // Handle click to open form
    this.$klaviyoFormTriggerButton.addEventListener('click', () => {
      window._klOnsite = window._klOnsite || []
      if (window.innerWidth < 768) {
        window._klOnsite.push(['openForm', klaviyoMobileFormId])
      } else {
        window._klOnsite.push(['openForm', klaviyoDesktopFormId])
      }
    })

    // Check if already submitted
    const klaviyoFormSubmittedId = localStorage.getItem('klaviyo-discount-form-submitted')
    if (
      klaviyoFormSubmittedId &&
      (klaviyoFormSubmittedId === klaviyoDesktopFormId || klaviyoFormSubmittedId === klaviyoMobileFormId)
    ) {
      this.updateContentAfterKlaviyoSubmit(klaviyoFormTriggerAfterText)
      document.body.classList.add('klaviyo-discount-form-submitted');
    }

    // Listen for form submission
    window.addEventListener('klaviyoForms', (e) => {
      if (e.detail.type === 'stepSubmit' && e.detail.metaData?.$step_name === 'SMS Opt-In') {
        localStorage.setItem('klaviyo-discount-form-submitted', e.detail.formId)
        this.updateContentAfterKlaviyoSubmit(klaviyoFormTriggerAfterText)
        document.body.classList.add('klaviyo-discount-form-submitted');
      }
    })
  }

  updateContentAfterKlaviyoSubmit(afterText) {
    this.$klaviyoFormTriggerButton.innerHTML = afterText
    this.$klaviyoFormTriggerButton.classList.remove('border-orange', 'border')
    this.$klaviyoFormTriggerButton.classList.add('cursor-default', 'pointer-events-none')
    this.$klaviyoFormTriggerButton.disabled = true

    if (this.config.klaviyoFormTriggerTextSubmittedPrice) {
      // Capture the current displayed price from the DOM before changing it
      const currentDisplayedPrice = this.$$price.length > 0
        ? this.$$price[0].textContent.trim()
        : null

      // Update prices
      const selectedPurchaseOption =
        this.$section.querySelector('[name="purchase-option"]:checked')?.value || 'product-only'

      this.$$price.forEach(($priceEl) => {
        $priceEl.textContent = this.config.klaviyoFormTriggerTextSubmittedPrice
      })

      if (this.$bundlePrice) {
        this.$bundlePrice.textContent = this.config.klaviyoFormTriggerTextSubmittedPrice
      }
      if (this.$onlyProductPrice) {
        this.$onlyProductPrice.textContent = this.config.klaviyoFormTriggerTextSubmittedPrice
      }

      // Update compare at prices — use current displayed price as compare-at
      const compareAtValue = selectedPurchaseOption === 'product-bundle'
        ? (this.config.bundleCompareAtPrice || currentDisplayedPrice)
        : (this.config.onlyProductPrice || currentDisplayedPrice)

      this.$compareAtPrice.forEach(($compareAt) => {
        $compareAt.textContent = compareAtValue
      })

      // Show fixed ATC compare-at price (hidden by default)
      const $fixedAtcCompareAt = this.$section.querySelector('[data-fixed-atc-compare-at]')
      if ($fixedAtcCompareAt) {
        $fixedAtcCompareAt.classList.remove('hidden')
      }

      // Swap price container class to discounted
      this.$section.querySelectorAll('.regular-price-container').forEach(($container) => {
        $container.classList.remove('regular-price-container')
        $container.classList.add('discounted-price-container')
      })

      // Update global price variables
      window.onlyProductPrice = this.config.klaviyoFormTriggerTextSubmittedPrice
      window.onlyProductCompareAtPrice = this.config.onlyProductPrice || currentDisplayedPrice
      window.bundlePrice = this.config.klaviyoFormTriggerTextSubmittedPrice
    }
  }

  // Affirm Management
  initAffirm() {
    if (typeof affirm === 'undefined') return

    affirm.ui.ready(() => {
      const $affirmElement = this.$section.querySelector('.affirm-as-low-as')
      if (!$affirmElement) return

      const observer = new MutationObserver((mutations, observer) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            this.setupAffirmElements(observer)
          }
        })
      })

      observer.observe($affirmElement, { childList: true })
    })
  }

  setupAffirmElements(observer) {
    const $affirmLink = this.$section.querySelector('.affirm-as-low-as a')
    const $affirmModalTrigger = this.$section.querySelector('.affirm-modal-trigger')
    const $affirmPriceElement = this.$section.querySelector('.affirm-ala-price')
    const $affirmAfterText = this.$section.querySelector('.affirm-after-text')
    const $$productOnlyAffirmText = this.$section.querySelectorAll('[data-product-only-affirm-text]')
    const $$affirmAmount = this.$section.querySelectorAll('[data-affirm-amount]')

    // Check if all required elements exist
    if (
      !$affirmLink ||
      !$affirmModalTrigger ||
      !$affirmPriceElement ||
      $$productOnlyAffirmText.length === 0 ||
      $$affirmAmount.length === 0 ||
      !$affirmAfterText
    ) {
      return
    }

    observer.disconnect()

    // Set up Affirm elements
    $affirmLink.before($affirmAfterText)
    $affirmModalTrigger.classList.add('text-orange', 'underline')

    const affirmAsLowAsAmount = $affirmPriceElement.textContent
    $$affirmAmount.forEach(($affirmAmount) => {
      $affirmAmount.textContent = affirmAsLowAsAmount
    })
    $$productOnlyAffirmText.forEach(($productOnlyAffirmText) => {
      $productOnlyAffirmText.classList.remove('hidden')
    })
  }

  handleAffirmForProductOnly() {
    affirm.ui.ready(() => {
      const $affirmAfterText = this.$section.querySelector('.affirm-after-text')
      const $affirmAsLowAs = this.$section.querySelector('.affirm-as-low-as')

      if ($affirmAsLowAs) {
        $affirmAsLowAs.classList.remove('text-gray-2')
      }
      if ($affirmAfterText) {
        $affirmAfterText.dataset.originalText = $affirmAfterText.textContent
        $affirmAfterText.textContent = ''
      }
    })
  }

  handleAffirmForBundle() {
    affirm.ui.ready(() => {
      const $affirmAfterText = this.$section.querySelector('.affirm-after-text')
      const $affirmAsLowAs = this.$section.querySelector('.affirm-as-low-as')

      if ($affirmAsLowAs) {
        $affirmAsLowAs.classList.add('text-gray-2')
      }
      if ($affirmAfterText && $affirmAfterText.dataset.originalText) {
        $affirmAfterText.textContent = $affirmAfterText.dataset.originalText
      }
    })
  }

  // Add to Cart Management
  initAddToCart() {
    if (!this.$$addToCartButtons.length) return

    this.$$addToCartButtons.forEach(($addToCartButton) => {
      $addToCartButton.addEventListener('click', () => {
        this.handleAddToCart($addToCartButton)
      })
    })
  }

  handleAddToCart($button) {
    if ($button.disabled) return

    const $spinner = $button.querySelector('svg')
    const $span = $button.querySelector('span')
    const $error = $button.parentElement.querySelector('[data-add-to-cart-error]')

    $span.classList.add('invisible')
    $spinner.classList.remove('hidden')

    const path = window.location.pathname

    if (path === '/pages/shaving-lander') {
      window.location.href = this.config.productUrl
      return
    }

    const purchaseOption = this.$section.querySelector('[name="purchase-option"]:checked')?.value || 'product-only'
    const items = this.prepareCartItems(purchaseOption)

    // Check for upsell
    const $upsell = this.$section.querySelector('[data-upsell]')
    if ($upsell && !document.querySelector(`cart-item[data-item-handle="${$upsell.dataset.upsell}"]`)) {
      $upsell.addToCart = (items) => this.addToCart(items, $span, $spinner, $error)
      $upsell.originalItems = items
      $upsell.classList.remove('opacity-0', 'pointer-events-none')
    } else {
      this.addToCart(items, $span, $spinner, $error)
    }
  }

  prepareCartItems(purchaseOption) {
    const $$selectedProductOptions = this.$section.querySelectorAll('[name^="option"]:checked')
    const selectedProductOptions = [...$$selectedProductOptions].map(($opt) => $opt.value)

    const selectedVariant = this.config.variants.find((variant) => {
      return selectedProductOptions.every((option, index) => {
        return variant.options[index] === option
      })
    })

    const mainItems = [
      {
        id: selectedVariant.id,
        quantity: 1,
        ...(this.config.templateSuffix === 'v2-flasher-149' && {
          properties: { _flash_only_149: true },
        }),
        ...((purchaseOption === 'subscription' || purchaseOption === 'product-bundle') &&
          this.config.sellingPlanId && {
            selling_plan: this.config.sellingPlanId,
          }),
      },
    ]

    if (purchaseOption === 'product-bundle') {
      return this.prepareBundleItems(mainItems, selectedVariant)
    }

    return mainItems
  }

  prepareBundleItems(mainItems, selectedVariant) {
    // Special handling for noodist kit
    if (this.config.productId === 8665317540090) {
      return [
        { id: 46536614248698, quantity: 1, selling_plan: 4086989050, properties: { _noodist_kit_bundle: true } },
        { id: 46544247750906, quantity: 1, selling_plan: 4042031354, properties: { _noodist_kit_bundle: true } },
        { id: 46544276062458, quantity: 1, selling_plan: 4131717370, properties: { _noodist_kit_bundle: true } },
        {
          id: selectedVariant.id === 46872959123706 ? 44847278620922 : 44847278653690,
          quantity: 1,
          properties: { _noodist_kit_bundle: true },
        },
        {
          id: selectedVariant.id === 46872959123706 ? 47614699995386 : 47614699962618,
          quantity: 1,
          properties: { _noodist_kit_bundle: true },
        },
      ]
    }

    // Regular bundle handling
    const bundleItems = []

    if (this.config.bundleDiscountScriptProperty) {
      bundleItems.push({
        id: 46544247750906, // nood serum
        quantity: this.config.templateSuffix === 'v2-flasher-pro' ? 2 : 1,
        selling_plan: 4042031354, // 60 day
        properties: {
          [this.config.bundleDiscountScriptProperty]: true,
        },
      })

      // Add bundle property to main product
      mainItems[0].properties = {
        ...mainItems[0].properties,
        [this.config.bundleDiscountScriptProperty]: true,
      }
    }

    return [...bundleItems, ...mainItems]
  }

  addToCart(items, $span, $spinner, $error) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 404) {
          if ($error) {
            $error.textContent = data.description
            $error.classList.remove('hidden')
          }
        } else {
          const $cart = document.querySelector('nood-cart')
          if ($cart) $cart.open()
        }
      })
      .catch((error) => {
        console.error('Error:', error)
        if ($error) {
          $error.textContent = error.message
          $error.classList.remove('hidden')
        }
      })
      .finally(() => {
        $span.classList.remove('invisible')
        $spinner.classList.add('hidden')
      })
  }

  // Addon Add to Cart
  initAddonAddToCart() {
    if (!this.$$addonAddToCartButtons.length) return

    this.$$addonAddToCartButtons.forEach(($button) => {
      $button.addEventListener('click', () => {
        this.handleAddonAddToCart($button)
      })
    })
  }

  handleAddonAddToCart($button) {
    const $spinner = $button.querySelector('svg')
    const $span = $button.querySelector('span')

    $span.classList.add('invisible')
    $spinner.classList.remove('hidden')

    const variantId = parseInt($button.dataset.variantId)
    if (isNaN(variantId)) return

    const purchaseOption = this.$section.querySelector('[name="purchase-option"]:checked')?.value || 'one-time'
    const sellingPlan = parseInt($button.dataset.sellingPlan)

    const items = [
      {
        id: variantId,
        quantity: 1,
        ...(purchaseOption === 'subscription' && !isNaN(sellingPlan) && { selling_plan: sellingPlan }),
      },
    ]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('addon add to cart', data)
        if (data.status !== 404) {
          const $cart = document.querySelector('nood-cart')
          if ($cart) $cart.open()
        }
      })
      .catch((error) => {
        console.error('Error:', error)
      })
      .finally(() => {
        $span.classList.remove('invisible')
        $spinner.classList.add('hidden')
      })
  }

  // Accordion Management
  initAccordions() {
    if (!this.$accordionToggles.length) return

    this.$accordionToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        this.handleAccordionToggle(toggle)
      })
    })
  }

  handleAccordionToggle(toggle) {
    const content = toggle.nextElementSibling
    const icon = toggle.querySelector('[data-accordion-toggle-icon] line:first-child')

    if (content.style.gridTemplateRows === '1fr') {
      // Close this accordion
      content.style.gridTemplateRows = '0fr'
      icon.classList.remove('hidden')
      toggle.setAttribute('aria-expanded', 'false')
    } else {
      // Close all accordions first
      this.closeAllAccordions()
      // Open this accordion
      content.style.gridTemplateRows = '1fr'
      icon.classList.add('hidden')
      toggle.setAttribute('aria-expanded', 'true')
    }
  }

  closeAllAccordions() {
    this.$accordionToggles.forEach((toggle) => {
      const content = toggle.nextElementSibling
      const icon = toggle.querySelector('[data-accordion-toggle-icon] line:first-child')
      content.style.gridTemplateRows = '0fr'
      icon.classList.remove('hidden')
      toggle.setAttribute('aria-expanded', 'false')
    })
  }

  // FAQ Link Management
  initFaqLink() {
    if (!this.$faqLink) return

    const $faqSection = document.querySelector('#faq')
    if (!$faqSection) {
      this.$faqLink.remove()
    }
  }

  // Carousel Sync Method
  syncCarouselWithCurrentVariant() {
    // Wait a bit to ensure Shopify's variant initialization is complete
    setTimeout(() => {
      if (this.config.useBlocks) {
        this.updateCarouselImages()
      } else {
        this.goToVariantMedia()
      }
      this.updateShippingTextVisibility()
    }, 100)

    // Also retry a bit later to catch any slower initializations
    setTimeout(() => {
      if (this.config.useBlocks) {
        this.updateCarouselImages()
      } else {
        this.goToVariantMedia()
      }
      this.updateShippingTextVisibility()
    }, 500)
  }

  updateCarouselImages() {
    // Only filter when using image blocks
    if (!this.config.useBlocks) return

    const currentColor = this.getCurrentSelectedColor()
    const currentPurchaseOption =
      this.$section.querySelector('[name="purchase-option"]:checked')?.value || 'product-only'
    const currentLabel = currentPurchaseOption === 'product-bundle' ? 'product-bundle' : 'product-only'

    // Update carousel for both desktop and mobile
    const desktopSlider = this.$section.querySelector('.product-media-slider-desktop')
    const mobileSlider = this.$section.querySelector('.product-media-slider-mobile')

    if (desktopSlider && typeof desktopSlider.filterSlidesByLabelAndColor === 'function') {
      desktopSlider.filterSlidesByLabelAndColor(currentLabel, currentColor)
    }

    if (mobileSlider && typeof mobileSlider.filterSlidesByLabelAndColor === 'function') {
      mobileSlider.filterSlidesByLabelAndColor(currentLabel, currentColor)
    }
  }

  // Utility Methods
  handleizeColor(colorValue) {
    // "Gloss White" -> "gloss-white", "Steel Blue" -> "steel-blue", etc.
    if (!colorValue) return ''
    return colorValue.toLowerCase().replace(/\s+/g, '-')
  }

  getCurrentSelectedColor() {
    const selectedColorOption = this.$section.querySelector('[name^="option"][data-option-name="color"]:checked')
    if (selectedColorOption) {
      return this.handleizeColor(selectedColorOption.value)
    }
    return null
  }

  getSelectedVariant() {
    const $$selectedOptions = this.$section.querySelectorAll('[name^="option"]:checked')
    const selectedOptions = [...$$selectedOptions].map(($opt) => $opt.value)

    return this.config.variants.find((variant) => {
      return selectedOptions.every((option, index) => variant.options[index] === option)
    })
  }

  goToVariantMedia() {
    const selectedVariant = this.getSelectedVariant()
    if (!selectedVariant || !this.config.variantMedia) return

    const mediaId = this.config.variantMedia[selectedVariant.id]
    if (!mediaId) return

    const desktopSlider = this.$section.querySelector('.product-media-slider-desktop')
    const mobileSlider = this.$section.querySelector('.product-media-slider-mobile')

    if (desktopSlider?.goToMediaId) {
      desktopSlider.goToMediaId(mediaId)
    }
    if (mobileSlider?.goToMediaId) {
      mobileSlider.goToMediaId(mediaId)
    }
  }
}

// Global helper function
window.getCurrentSelectedColor = function () {
  const $section = document.querySelector('#purchase-options')
  if (!$section) return null

  const selectedColorOption = $section.querySelector('[name^="option"][data-option-name="color"]:checked')
  if (selectedColorOption) {
    // Handleize: "Gloss White" -> "gloss-white"
    return selectedColorOption.value.toLowerCase().replace(/\s+/g, '-')
  }
  return null
}

// Export for use
window.V2ProductBundle = V2ProductBundle
